import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBuyerSelector } from 'src/components/Selectors/BuyerSelector/useBuyerSelector';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { DetailSection, SectionContent } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import { posChangedField } from 'src/utils/posFieldUtils';
import {
  Feature,
  Marketplace,
  MarketplacePaymentState,
  PosManagedMarketplaceSaleInput,
  SaleDetails,
} from 'src/WebApiController';

type MarketplaceSaleInputBuyer = Pick<
  PosManagedMarketplaceSaleInput,
  'marketplaceSaleId' | 'buyerEmail' | 'buyerName' | 'buyerPhone' | 'saleId'
>;

export const BuyerSection = ({
  disabled,
  saleReadOnly,
}: {
  disabled?: boolean;
  saleReadOnly?: SaleDetails;
}) => {
  const requiredMsg = useContent(ContentId.Required);
  const isMobile = useMatchMedia('mobile');

  const { register, formState, setValue, getValues, clearErrors } =
    useFormContext<MarketplaceSaleInputBuyer>();

  const { buyerEmail } = getValues();
  const { query, posSelectProps } = useBuyerSelector({
    disabled,
    placeholderText: ContentId.AddNewBuyer,
  });
  const [selectedBuyer, setSelectedBuyer] = useState<string>(
    buyerEmail?.value ?? ''
  );

  const isEdit = formState?.defaultValues?.saleId != null;
  const buyerEmailError = formState.errors.buyerEmail?.message;
  const orderIdError = formState.errors.marketplaceSaleId?.message;

  const hasEditMarketplaceSaleId_OfflineFeature = useUserHasFeature(
    Feature.EditMarketplaceSaleId_Offline
  );

  const hasEditMarketplaceSaleId_MarketplaceFeature = useUserHasFeature(
    Feature.EditMarketplaceSaleId_Marketplace
  );
  const isOfflineSale = saleReadOnly?.mkp === Marketplace.Offline;

  // Don't allow editing marketplaceSaleId if the sale is already paid or partially paid. This will affect the payment
  // TODO in https://thestubhub.atlassian.net/browse/POS-6015
  const canEditSaleId =
    ((isOfflineSale && hasEditMarketplaceSaleId_OfflineFeature) ||
      (!isOfflineSale && hasEditMarketplaceSaleId_MarketplaceFeature)) &&
    saleReadOnly?.mkpPmtState !== MarketplacePaymentState.Paid &&
    saleReadOnly?.mkpPmtState !== MarketplacePaymentState.PartiallyPaid;

  const isEditMarketplaceSale = isEdit && !isOfflineSale;

  return (
    <DetailSection name={<Content id={ContentId.YourBuyer} />}>
      <SectionContent
        style={{ gridTemplateColumns: `repeat(${isMobile ? 1 : 2}, 1fr)` }}
      >
        <PosFormField
          label={<Content id={ContentId.ExternalSaleId} />}
          errors={orderIdError}
        >
          <PosTextField
            inputMode="email"
            disabled={disabled || (isEdit && !canEditSaleId)}
            rootProps={{
              state: getTextFieldState(orderIdError),
            }}
            trimOnBlur
            {...register('marketplaceSaleId.value', {
              required: requiredMsg,
              onChange: ({ target: { value } }) => {
                setValue('marketplaceSaleId', posChangedField(value));
              },
            })}
          />
        </PosFormField>
        {!isEditMarketplaceSale && (
          <>
            <PosFormField label={<Content id={ContentId.ChooseBuyer} />}>
              <PosSelect
                disabled={disabled || isEditMarketplaceSale}
                {...posSelectProps}
                style={{ width: '100%' }}
                enableEmptySelection
                value={selectedBuyer}
                onChange={(newSelection) => {
                  setSelectedBuyer(newSelection);
                  if (newSelection !== 'AddNewBuyer') {
                    clearErrors('buyerEmail');
                    setValue('buyerEmail', posChangedField(newSelection));
                    const buyer = query.data?.find(
                      (b) => b.email === newSelection
                    );
                    if (buyer) {
                      setValue('buyerName', posChangedField(buyer.name));
                      setValue('buyerPhone', posChangedField(buyer.phone));
                    }
                  }
                }}
              />
            </PosFormField>
            <PosFormField
              label={<Content id={ContentId.Email} />}
              errors={buyerEmailError}
            >
              <PosTextField
                inputMode="email"
                disabled={disabled || isEditMarketplaceSale}
                rootProps={{
                  state: getTextFieldState(buyerEmailError),
                }}
                {...register('buyerEmail.value', {
                  required: requiredMsg,
                  onChange: ({ target: { value } }) => {
                    const buyer = query.data?.find((b) => b.email === value);
                    if (!buyer) {
                      setSelectedBuyer('');
                    } else {
                      setValue('buyerEmail', posChangedField(value));
                      setValue('buyerName', posChangedField(buyer.name));
                      setValue('buyerPhone', posChangedField(buyer.phone));
                      setSelectedBuyer(value);
                    }
                  },
                })}
              />
            </PosFormField>
            <PosFormField label={<Content id={ContentId.Name} />}>
              <PosTextField
                disabled={disabled || isEditMarketplaceSale}
                {...register('buyerName.value', {
                  required: requiredMsg,
                  onChange: ({ target: { value } }) => {
                    setValue('buyerName', posChangedField(value));
                  },
                })}
              />
            </PosFormField>
            <PosFormField label={<Content id={ContentId.Phone} />}>
              <PosTextField
                disabled={disabled || isEditMarketplaceSale}
                inputMode="tel"
                {...register('buyerPhone.value', {
                  required: requiredMsg,
                  onChange: ({ target: { value } }) => {
                    setValue('buyerPhone', posChangedField(value));
                  },
                })}
              />
            </PosFormField>
          </>
        )}
      </SectionContent>
    </DetailSection>
  );
};
